import { useState } from 'react'

import { Drawer } from 'core/components'

import { aplicarMascaraTelefone } from './mascara-telefone'
import {
  Actions,
  CodeField,
  ErrorText,
  Field,
  Form,
  Hint,
  NoticeText,
  ResendActions,
  StepLabel,
  Title,
  WarningText,
} from './styles'
import { PhoneChangeStep, usePhoneChange } from './use-phone-change'

export type PhoneChangeDrawerProps = {
  currentPhone: string
  onHide: () => void
  onSuccess: () => void
}

// O passo 'done' tambem e usado quando o confirm nao respondeu: nesse caso nao
// se sabe se a troca ocorreu, e nada na tela pode afirmar que sim.
const TITULO_INDETERMINADO = 'Não foi possível confirmar'

const TITULO_SAIR = 'Sair sem concluir?'

const TITULOS: Record<PhoneChangeStep, string> = {
  loading: 'Trocar telefone',
  password: 'Confirme sua senha',
  phone: 'Novo número',
  code: 'Código de verificação',
  done: 'Telefone alterado',
}

function PhoneChangeDrawer({
  currentPhone,
  onHide,
  onSuccess,
}: PhoneChangeDrawerProps) {
  const {
    step,
    requiresPassword,
    loading,
    error,
    destination,
    syncWarning,
    changeConfirmed,
    resending,
    resendCooldown,
    resendsLeft,
    resendNotice,
    submitPassword,
    submitPhone,
    submitCode,
    resendCode,
  } = usePhoneChange()

  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [confirmandoSaida, setConfirmandoSaida] = useState(false)

  // Um SMS custa uma das 3 solicitacoes por hora: nao deixa submeter um numero
  // que o backend recusaria de qualquer jeito.
  const telefoneIncompleto =
    phone.replace(/\D/g, '').length < 10 || phone.replace(/\D/g, '').length > 11

  // Fechar sem nada preenchido nao custa nada ao usuario: perguntar ali seria
  // so um clique a mais. A partir do momento em que ha senha digitada — e
  // principalmente depois do SMS, que consome uma das 3 solicitacoes por hora —
  // a saida acidental tem preco, e ai vale confirmar.
  const temProgresso =
    (step !== 'loading' && step !== 'password' && step !== 'phone') ||
    password.length > 0 ||
    phone.length > 0

  // Conta sem senha pula o primeiro passo, entao a contagem tambem encolhe:
  // "Passo 2 de 3" numa tela que so tem duas faz o usuario procurar a que
  // falta.
  const totalDePassos = requiresPassword ? 3 : 2
  const passoAtual =
    step === 'password' ? 1 : step === 'phone' ? totalDePassos - 1 : totalDePassos

  // So intercepta as saidas acidentais (backdrop, arrasto, Escape). O botao
  // 'Cancelar' e uma decisao explicita: perguntar de novo ali seria ruido.
  const pedirConfirmacaoDeSaida = () => {
    if (!temProgresso) {
      onHide()
      return
    }

    setConfirmandoSaida(true)
  }

  const semReenvios = resendsLeft === 0
  const reenvioBloqueado =
    loading || resending !== null || resendCooldown > 0 || semReenvios

  // Durante a espera o botão mostra a contagem no lugar do rótulo, como o
  // modal de verificação do orul-auth faz no cadastro.
  const rotuloDoBotao = (rotulo: string, metodo: 'sms' | 'voice-call') => {
    if (resending === metodo) return 'Enviando...'
    if (resendCooldown > 0) return `${rotulo} (${resendCooldown}s)`

    return rotulo
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (step === 'password') {
      submitPassword(password)
      return
    }
    if (step === 'phone') {
      await submitPhone(phone)
      return
    }
    if (step === 'code') {
      await submitCode(code)
    }
  }

  return (
    // No passo 'done' o drawer deixa de fechar por backdrop, arrasto ou Escape.
    // Depois de um confirm ambiguo, o aviso na tela e a unica informacao que o
    // usuario tem sobre uma troca que pode ter acontecido — e bottom sheet
    // fecha por acidente com facilidade. Sai pelo botao, ou nao sai.
    //
    // Com a pergunta de saida aberta vale o mesmo: senao o proprio clique fora
    // que a abriu seguiria fechando o drawer por baixo dela.
    <Drawer
      onHide={pedirConfirmacaoDeSaida}
      dismissible={step !== 'done' && !confirmandoSaida}
    >
      <Title>
        {confirmandoSaida
          ? TITULO_SAIR
          : step === 'done' && !changeConfirmed
            ? TITULO_INDETERMINADO
            : TITULOS[step]}
      </Title>
      {confirmandoSaida ? (
        <Form onSubmit={event => event.preventDefault()}>
          <Hint>
            A troca em andamento será descartada e você precisará começar de
            novo. Cada novo código conta no limite de 3 envios por hora.
          </Hint>
          <Actions>
            <button type="button" onClick={() => setConfirmandoSaida(false)}>
              Continuar troca
            </button>
            <button type="submit" onClick={onHide}>
              Sair mesmo assim
            </button>
          </Actions>
        </Form>
      ) : step === 'loading' ? (
        <Form onSubmit={event => event.preventDefault()}>
          <Hint>Carregando...</Hint>
          <Actions>
            <button type="button" onClick={onHide}>
              Cancelar
            </button>
          </Actions>
        </Form>
      ) : step === 'done' ? (
        <Form onSubmit={event => event.preventDefault()}>
          {changeConfirmed && (
            <Hint>Seu login passa a usar o novo número a partir de agora.</Hint>
          )}
          {syncWarning && <WarningText>{syncWarning}</WarningText>}
          <Actions>
            <button type="submit" onClick={onSuccess}>
              Fechar
            </button>
          </Actions>
        </Form>
      ) : (
        <Form onSubmit={handleSubmit}>
          <StepLabel>
            Passo {passoAtual} de {totalDePassos}
          </StepLabel>

          {step === 'password' && (
            <>
              <Hint>Telefone atual: {currentPhone}</Hint>
              <Field
                type="password"
                placeholder="Senha atual"
                value={password}
                onChange={event => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </>
          )}

          {step === 'phone' && (
            <>
              <Hint>Enviaremos um código por SMS para o novo número.</Hint>
              <Field
                type="tel"
                placeholder="(00) 0 0000-0000"
                value={phone}
                inputMode="numeric"
                onChange={event =>
                  setPhone(anterior =>
                    aplicarMascaraTelefone(event.target.value, anterior),
                  )
                }
              />
            </>
          )}

          {step === 'code' && (
            <>
              <Hint>Enviamos um código para {destination}.</Hint>
              <CodeField
                type="tel"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={event =>
                  setCode(event.target.value.replace(/\D/g, ''))
                }
              />

              {/*
               * Os dois métodos ficam disponíveis desde o início, como no
               * cadastro do orul-auth: quem já sabe que não recebe SMS nesse
               * chip não precisa esperar dois ciclos para tentar a ligação. O
               * que contém o custo é o intervalo entre um pedido e o próximo.
               *
               * Um pedido em andamento desabilita OS DOIS — enviar SMS e
               * ligação ao mesmo tempo gera dois códigos, e só o último vale.
               */}
              <ResendActions>
                <button
                  type="button"
                  onClick={() => resendCode('sms')}
                  disabled={reenvioBloqueado}
                >
                  {rotuloDoBotao('Reenviar SMS', 'sms')}
                </button>
                <button
                  type="button"
                  onClick={() => resendCode('voice-call')}
                  disabled={reenvioBloqueado}
                >
                  {rotuloDoBotao('Receber ligação', 'voice-call')}
                </button>
              </ResendActions>

              {resendNotice && <NoticeText>{resendNotice}</NoticeText>}
              {resendsLeft === 0 && (
                <Hint>
                  Você usou os reenvios desta solicitação. Se ainda não
                  recebeu, cancele e comece de novo.
                </Hint>
              )}
            </>
          )}

          {error && <ErrorText>{error}</ErrorText>}

          <Actions>
            <button type="button" onClick={onHide} disabled={loading}>
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || (step === 'phone' && telefoneIncompleto)}
            >
              {loading ? 'Aguarde...' : 'Continuar'}
            </button>
          </Actions>
        </Form>
      )}
    </Drawer>
  )
}

export default PhoneChangeDrawer
